import { useConnectionState, useRoomContext } from '@livekit/components-react'
import { ConnectionState, type Room } from 'livekit-client'
import { useCallback, useEffect, useRef } from 'react'

type LiveKitViewerTelemetryProps = {
  eventId?: string | null
  viewerId?: string | null
  viewerIdentity?: string | null
  surface: string
  enabled?: boolean
}

type PeerTransport = {
  getStats?: () => Promise<RTCStatsReport>
  getConnectionState?: () => RTCPeerConnectionState
  getICEConnectionState?: () => RTCIceConnectionState
}

type PeerStatsSummary = {
  peer_connection_state?: string | null
  ice_connection_state?: string | null
  selected_candidate_protocol?: string | null
  selected_candidate_type?: string | null
  remote_candidate_protocol?: string | null
  remote_candidate_type?: string | null
  selected_candidate_pair_state?: string | null
  candidate_protocols: string[]
  local_candidate_protocols: string[]
  remote_candidate_protocols: string[]
  candidate_pair_states: Record<string, number>
  candidate_pair_count: number
  failed_candidate_pair_count: number
  succeeded_candidate_pair_count: number
  tcp_candidate_available: boolean
}

type NetworkInformationLike = {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
}

const HEALTHY_SAMPLE_INTERVAL_MS = 60_000
const CONNECTED_STATS_DELAY_MS = 2_500

export function LiveKitViewerTelemetry({
  eventId,
  viewerId,
  viewerIdentity,
  surface,
  enabled = true,
}: LiveKitViewerTelemetryProps) {
  const room = useRoomContext()
  const connectionState = useConnectionState()
  const hasConnectedRef = useRef(false)
  const hasAttemptedConnectionRef = useRef(false)
  const connectedAtRef = useRef<number | null>(null)
  const lastFailureSignatureRef = useRef<string | null>(null)

  const sendTelemetry = useCallback(async (failurePhase?: string) => {
    if (!enabled || !eventId) return

    const peerStats = await collectPeerStats(room)
    const payload = {
      surface,
      viewer_id: viewerId,
      viewer_identity: viewerIdentity,
      connection_state: connectionState,
      has_connected: hasConnectedRef.current,
      connected_duration_ms: connectedDurationMs(connectedAtRef.current),
      failure_phase: failurePhase,
      browser: detectBrowser(),
      network: networkInfo(),
      ...peerStats,
    }

    postViewerConnectionTelemetry(eventId, payload)
  }, [connectionState, enabled, eventId, room, surface, viewerId, viewerIdentity])

  useEffect(() => {
    if (!enabled || !eventId) return

    if (connectionState === ConnectionState.Connecting) {
      hasAttemptedConnectionRef.current = true
      return
    }

    if (connectionState === ConnectionState.Connected) {
      hasAttemptedConnectionRef.current = true
      hasConnectedRef.current = true
      connectedAtRef.current ||= Date.now()
      lastFailureSignatureRef.current = null

      const timeout = window.setTimeout(() => {
        void sendTelemetry()
      }, CONNECTED_STATS_DELAY_MS)

      return () => window.clearTimeout(timeout)
    }

    if (
      connectionState === ConnectionState.Reconnecting ||
      connectionState === ConnectionState.SignalReconnecting ||
      connectionState === ConnectionState.Disconnected
    ) {
      if (!hasAttemptedConnectionRef.current && !hasConnectedRef.current) return

      const signature = `${eventId}:${connectionState}:${hasConnectedRef.current}:${connectedAtRef.current ?? 'never'}`
      if (lastFailureSignatureRef.current !== signature) {
        lastFailureSignatureRef.current = signature
        void sendTelemetry(connectionState)
      }
    }
  }, [connectionState, enabled, eventId, sendTelemetry])

  useEffect(() => {
    if (!enabled || !eventId || connectionState !== ConnectionState.Connected) return

    const interval = window.setInterval(() => {
      void sendTelemetry()
    }, HEALTHY_SAMPLE_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [connectionState, enabled, eventId, sendTelemetry])

  return null
}

async function collectPeerStats(room: Room): Promise<PeerStatsSummary> {
  const transports = getPeerTransports(room)
  const summaries = await Promise.all(transports.map((transport) => collectTransportStats(transport)))

  return summaries.reduce<PeerStatsSummary>((acc, summary) => ({
    peer_connection_state: worstPeerState(acc.peer_connection_state, summary.peer_connection_state),
    ice_connection_state: worstIceState(acc.ice_connection_state, summary.ice_connection_state),
    selected_candidate_protocol: acc.selected_candidate_protocol ?? summary.selected_candidate_protocol,
    selected_candidate_type: acc.selected_candidate_type ?? summary.selected_candidate_type,
    remote_candidate_protocol: acc.remote_candidate_protocol ?? summary.remote_candidate_protocol,
    remote_candidate_type: acc.remote_candidate_type ?? summary.remote_candidate_type,
    selected_candidate_pair_state: acc.selected_candidate_pair_state ?? summary.selected_candidate_pair_state,
    candidate_protocols: unique([...acc.candidate_protocols, ...summary.candidate_protocols]),
    local_candidate_protocols: unique([...acc.local_candidate_protocols, ...summary.local_candidate_protocols]),
    remote_candidate_protocols: unique([...acc.remote_candidate_protocols, ...summary.remote_candidate_protocols]),
    candidate_pair_states: mergeCounts(acc.candidate_pair_states, summary.candidate_pair_states),
    candidate_pair_count: acc.candidate_pair_count + summary.candidate_pair_count,
    failed_candidate_pair_count: acc.failed_candidate_pair_count + summary.failed_candidate_pair_count,
    succeeded_candidate_pair_count: acc.succeeded_candidate_pair_count + summary.succeeded_candidate_pair_count,
    tcp_candidate_available: acc.tcp_candidate_available || summary.tcp_candidate_available,
  }), emptyPeerStats())
}

function getPeerTransports(room: Room): PeerTransport[] {
  const roomWithEngine = room as Room & {
    engine?: {
      pcManager?: {
        subscriber?: PeerTransport
        publisher?: PeerTransport
      }
    }
  }
  const pcManager = roomWithEngine.engine?.pcManager
  const transports: Array<PeerTransport | undefined> = [
    pcManager?.subscriber as PeerTransport | undefined,
    pcManager?.publisher as PeerTransport | undefined,
  ]

  return transports.filter((transport): transport is PeerTransport => {
    return Boolean(transport && typeof transport.getStats === 'function')
  })
}

async function collectTransportStats(transport: PeerTransport): Promise<PeerStatsSummary> {
  const summary = emptyPeerStats()
  summary.peer_connection_state = transport.getConnectionState?.() ?? null
  summary.ice_connection_state = transport.getICEConnectionState?.() ?? null

  if (!transport.getStats) return summary

  const report = await transport.getStats()
  let selectedCandidatePairId: string | null = null
  const candidatePairs = new Map<string, Record<string, unknown>>()
  const localCandidates = new Map<string, Record<string, unknown>>()
  const remoteCandidates = new Map<string, Record<string, unknown>>()

  report.forEach((stat) => {
    const entry = stat as RTCStats & Record<string, unknown>

    if (entry.type === 'transport' && typeof entry.selectedCandidatePairId === 'string') {
      selectedCandidatePairId = entry.selectedCandidatePairId
    } else if (entry.type === 'candidate-pair') {
      candidatePairs.set(entry.id, entry)
    } else if (entry.type === 'local-candidate') {
      localCandidates.set(entry.id, entry)
      addProtocol(summary.local_candidate_protocols, entry.protocol)
      addProtocol(summary.candidate_protocols, entry.protocol)
    } else if (entry.type === 'remote-candidate') {
      remoteCandidates.set(entry.id, entry)
      addProtocol(summary.remote_candidate_protocols, entry.protocol)
      addProtocol(summary.candidate_protocols, entry.protocol)
    }
  })

  candidatePairs.forEach((pair, id) => {
    const state = typeof pair.state === 'string' ? pair.state : 'unknown'
    summary.candidate_pair_count += 1
    summary.candidate_pair_states[state] = (summary.candidate_pair_states[state] ?? 0) + 1
    if (state === 'failed') summary.failed_candidate_pair_count += 1
    if (state === 'succeeded') summary.succeeded_candidate_pair_count += 1

    if (!selectedCandidatePairId && (pair.selected === true || pair.nominated === true)) {
      selectedCandidatePairId = id
    }
  })

  const selectedPair = selectedCandidatePairId ? candidatePairs.get(selectedCandidatePairId) : null
  const localCandidate = candidateForPair(selectedPair, localCandidates, 'localCandidateId')
  const remoteCandidate = candidateForPair(selectedPair, remoteCandidates, 'remoteCandidateId')

  summary.selected_candidate_protocol = protocolFrom(localCandidate) ?? protocolFrom(remoteCandidate)
  summary.selected_candidate_type = candidateTypeFrom(localCandidate) ?? candidateTypeFrom(remoteCandidate)
  summary.remote_candidate_protocol = protocolFrom(remoteCandidate)
  summary.remote_candidate_type = candidateTypeFrom(remoteCandidate)
  summary.selected_candidate_pair_state = typeof selectedPair?.state === 'string' ? selectedPair.state : null
  summary.candidate_protocols = unique(summary.candidate_protocols)
  summary.local_candidate_protocols = unique(summary.local_candidate_protocols)
  summary.remote_candidate_protocols = unique(summary.remote_candidate_protocols)
  summary.tcp_candidate_available = summary.candidate_protocols.includes('tcp')

  return summary
}

function emptyPeerStats(): PeerStatsSummary {
  return {
    candidate_protocols: [],
    local_candidate_protocols: [],
    remote_candidate_protocols: [],
    candidate_pair_states: {},
    candidate_pair_count: 0,
    failed_candidate_pair_count: 0,
    succeeded_candidate_pair_count: 0,
    tcp_candidate_available: false,
  }
}

function candidateForPair(
  pair: Record<string, unknown> | null | undefined,
  candidates: Map<string, Record<string, unknown>>,
  key: 'localCandidateId' | 'remoteCandidateId',
) {
  const id = pair?.[key]
  if (typeof id !== 'string') return null
  return candidates.get(id) ?? null
}

function addProtocol(protocols: string[], value: unknown) {
  const protocol = normalizeProtocol(value)
  if (protocol && !protocols.includes(protocol)) protocols.push(protocol)
}

function protocolFrom(candidate: Record<string, unknown> | null | undefined) {
  return normalizeProtocol(candidate?.protocol)
}

function candidateTypeFrom(candidate: Record<string, unknown> | null | undefined) {
  const type = candidate?.candidateType
  return typeof type === 'string' ? type : null
}

function normalizeProtocol(value: unknown) {
  if (typeof value !== 'string') return null
  const protocol = value.toLowerCase()
  return ['udp', 'tcp', 'tls'].includes(protocol) ? protocol : null
}

function mergeCounts(left: Record<string, number>, right: Record<string, number>) {
  const merged = { ...left }
  Object.entries(right).forEach(([key, value]) => {
    merged[key] = (merged[key] ?? 0) + value
  })
  return merged
}

function unique(values: string[]) {
  return Array.from(new Set(values))
}

function connectedDurationMs(connectedAt: number | null) {
  if (!connectedAt) return 0
  return Math.max(0, Date.now() - connectedAt)
}

function worstPeerState(left?: string | null, right?: string | null) {
  return worstState(left, right, ['failed', 'disconnected', 'connecting', 'connected', 'new', 'closed'])
}

function worstIceState(left?: string | null, right?: string | null) {
  return worstState(left, right, ['failed', 'disconnected', 'checking', 'connected', 'completed', 'new', 'closed'])
}

function worstState(left: string | null | undefined, right: string | null | undefined, priority: string[]) {
  if (!left) return right ?? null
  if (!right) return left
  return priority.indexOf(right) < priority.indexOf(left) ? right : left
}

function detectBrowser() {
  const ua = window.navigator.userAgent
  const match =
    ua.match(/Edg\/([\d.]+)/) ? ['Edge', ua.match(/Edg\/([\d.]+)/)?.[1]] :
    ua.match(/Chrome\/([\d.]+)/) ? ['Chrome', ua.match(/Chrome\/([\d.]+)/)?.[1]] :
    ua.match(/Firefox\/([\d.]+)/) ? ['Firefox', ua.match(/Firefox\/([\d.]+)/)?.[1]] :
    ua.match(/Version\/([\d.]+).*Safari/) ? ['Safari', ua.match(/Version\/([\d.]+).*Safari/)?.[1]] :
    ['unknown', undefined]

  return {
    name: match[0],
    version: match[1],
  }
}

function networkInfo() {
  const connection = (window.navigator as Navigator & { connection?: NetworkInformationLike }).connection
  if (!connection) return {}

  return {
    effective_type: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    save_data: connection.saveData,
  }
}

function postViewerConnectionTelemetry(eventId: string, payload: Record<string, unknown>) {
  const body = JSON.stringify(payload)
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '/api/v1'
  const url = `${apiBase}/public/events/${encodeURIComponent(eventId)}/viewer_connection_events`

  void fetch(url, {
    method: 'POST',
    credentials: 'omit',
    keepalive: body.length < 60_000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body,
  }).catch(() => {
    // Telemetry must never affect playback.
  })
}
